import { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Typography,
  Divider,
  Link,
  Box,
  useTheme,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  DataGridPro,
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowSelectionModel,
  GridToolbarContainer,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { FormikProps } from 'formik';
import type { Item, InvoiceCreateInput, InvoiceItem } from '@repo/api-contract';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useDispatch, useSelector } from '../../../../state';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { openSearchItemModal } from '../../../../state/itemSearchModalSlice';
import {
  closeModal,
  openInvoiceModal,
} from '../../../../state/invoiceModalSlice';
import { openNewItemModal } from '../../../../state/itemModalSlice';
import BulkActionsMenu from '../../../utils/OrderActionsMenu';
import {
  EMPTY_LEADING_GRID_COLUMNS,
  GRID_CHECKBOX_COLUMN_ORDER,
  usePersistedDataGridColumnPreferences,
} from '../../../../util/hooks';
import type { DataGridTableId } from '../../../../util/dataGridTableIds';
import { GRID_CHECKBOX_SELECTION_FIELD } from '@mui/x-data-grid';
import { DataGridTranslatedColumnHeader } from '../../../dataGrid/DataGridTranslatedColumnHeader';

const TABLE_ID = 'invoice-items' satisfies DataGridTableId;
const GLOBAL_COMMON_COL_I18N = 'team.Global.Common';
const GLOBAL_BUTTONS_COL_I18N = 'team.Global.Buttons';
const ADD_INVOICE_COL_I18N = 'team.Modals.Invoices.createAndEdit.Add Invoice';
const columnPadding = '1 2px';

export default function Items({
  formik,
  viewMode,
}: {
  formik: FormikProps<InvoiceCreateInput>;
  viewMode: boolean;
}): JSX.Element {
  const { t: tAddI } = useTranslation([], {
    keyPrefix: 'team.Modals.Invoices.createAndEdit.Add Invoice',
  });

  const theme = useTheme();
  const dispatch = useDispatch();

  const { isOpenInvoiceModal, formData } = useSelector(
    ({ invoiceModalSlice }) => invoiceModalSlice,
  );

  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {},
  );
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    [],
  );
  const apiRef = useGridApiRef();

  const genUUID = () => {
    let uuid = uuidv4();
    return uuid;
  };

  const onImport = (selectedItems: Item[]) => {
    // Extract current item IDs to identify duplicates
    const currentItemIds = new Set(formData.items.map((item) => item.itemId));

    // Mapping Items to Invoice Item
    const mappedInvoiceItems = selectedItems
      .map((item) => {
        return {
          description: item.details.description,
          quantityOrdered: 0,
          quantityShipped: 0,
          unitNumber: item.details.unit,
          itemId: item.ItemId,
          taxable: item.details.taxable,
          vendorItemNumber: item.details.vendorItemNumber,
          customerItemNumber: item.details.customerItemNumber || '',
          vendorCost: item.details.vendorCost ?? 0,
          customerPrice: item.details.customerPrice,
          commissionable: item.details.commissionable,
          commissionRate: item.details.commissionRate,
        };
      })
      .filter((invoiceItem) => !currentItemIds.has(invoiceItem.itemId)); // Exclude duplicates

    dispatch(openInvoiceModal());
    const updatedFromData = {
      ...formData,
      items: [
        ...formData.items, // keep previously imported items
        ...mappedInvoiceItems, // add new items that aren't duplicates
      ],
    };
    formik.setValues(updatedFromData);
  };

  const newItemCallBack = async (item: Item | undefined): Promise<void> => {
    dispatch(openInvoiceModal());
    if (!item) return;
    const mappedItem = {
      description: item.details.description,
      quantityOrdered: 0,
      quantityShipped: 0,
      unitNumber: item.details.unit,
      itemId: item.ItemId,
      taxable: item.details.taxable,
      vendorItemNumber: item.details.vendorItemNumber,
      customerItemNumber: item.details.customerItemNumber,
      vendorCost: item.details.vendorCost ?? 0,
      customerPrice: item.details.customerPrice,
      commissionable: item.details.commissionable,
      commissionRate: item.details.commissionRate,
    };

    formik.setFieldValue('items', [...formik.values.items, mappedItem]);
  };

  const onCancel = () => {
    dispatch(openInvoiceModal());
  };

  const handleAddRowClick = () => {
    const tempId = `Custom-${genUUID()}`;
    const newRow = {
      itemId: tempId,
      description: '',
      quantityOrdered: 0,
      quantityShipped: 0,
      unitNumber: '',
      taxable: true,
      vendorItemNumber: '',
      customerItemNumber: '',
      vendorCost: 0,
      customerPrice: 0,
      commissionable: false,
      commissionRate: 0.0,
    };
    // This is stupid but the reason we are setting Items directly and not through formik
    // is because formik is slow and setRowModesModel will give error of unfound record
    setItems([...formik.values.items, newRow]);
    formik.setFieldValue('items', [...formik.values.items, newRow]);

    setRowModesModel((prevModel) => ({
      ...prevModel,
      [tempId]: { mode: GridRowModes.Edit },
    }));
  };

  const handleImportClick = () => {
    dispatch(closeModal());
    dispatch(openSearchItemModal({ onImport, onCancel }));
  };

  const handleCreateClick = () => {
    dispatch(closeModal());
    dispatch(openNewItemModal({ createCallback: newItemCallBack }));
  };

  function EditToolbar(props: {
    selectionModel: GridRowSelectionModel;
    setSelectionModel: (m: GridRowSelectionModel) => void;
    items: any[];
    setItems: React.Dispatch<React.SetStateAction<any[]>>;
    formik: any;
    viewMode: boolean;
    onAddRow: () => void;
  }) {
    const {
      selectionModel,
      setSelectionModel,
      items,
      setItems,
      formik,
      viewMode,
      onAddRow,
    } = props;

    return (
      <GridToolbarContainer sx={{ justifyContent: 'flex-end' }}>
        <BulkActionsMenu
          selectedIds={selectionModel}
          clearSelection={() => setSelectionModel([])}
          items={items}
          setItems={setItems}
          formik={formik}
          viewMode={viewMode}
          onAddRow={onAddRow}
          importItemClick={handleImportClick}
          createItemClick={handleCreateClick}
        />
      </GridToolbarContainer>
    );
  }

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event,
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    formik.setFieldValue(
      'items',
      items.filter((item) => item.itemId !== id),
    );
    setItems(items.filter((item) => item.itemId !== id));
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    // Find the original row in items
    const originalRow = items.find((item) => item.itemId === newRow.id);

    if (!originalRow) {
      throw new Error('Original row not found.');
    }

    // Merge the original row with the updated data
    const updatedRow = {
      ...originalRow,
      ...newRow, // Update fields from newRow
      isNew: false, // Ensure isNew is set to false
    };

    const updatedItems = items.map((item) =>
      item.itemId === newRow.id ? updatedRow : item,
    );

    // Update the state with the modified row
    setItems(updatedItems);

    // Update Formik with the updated items
    formik.setFieldValue('items', updatedItems);

    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  // It does not let user to enter negative values and converts string to number so formik does not send string in api call
  const numberTypeController = (value: any, row: any) => {
    return value < 0 ? 0 : parseFloat(value);
  };

  const [actionsMenuAnchors, setActionsMenuAnchors] = React.useState<
    Record<string, HTMLElement | null>
  >({});

  const handleOpenActionsMenu =
    (id: GridRowId) => (event: React.MouseEvent<HTMLElement>) => {
      setActionsMenuAnchors((prev) => ({
        ...prev,
        [String(id)]: event.currentTarget,
      }));
    };

  const handleCloseActionsMenu = (id: GridRowId) => () => {
    setActionsMenuAnchors((prev) => ({
      ...prev,
      [String(id)]: null,
    }));
  };
  const baseColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'quantityOrdered',
        headerName: 'Qty. Ordered',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Qty. Ordered"
            params={params}
            keyPrefix={ADD_INVOICE_COL_I18N}
          />
        ),
        editable: true,
        type: 'number',
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        valueParser: numberTypeController,
      },
      {
        field: 'quantityShipped',
        headerName: 'Qty. Shipped',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Qty. Shipped"
            params={params}
            keyPrefix={ADD_INVOICE_COL_I18N}
          />
        ),
        editable: true,
        type: 'number',
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        valueParser: numberTypeController,
      },
      {
        field: 'unitNumber',
        headerName: 'Unit',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Unit"
            params={params}
            keyPrefix={ADD_INVOICE_COL_I18N}
          />
        ),
        editable: true,
        flex: 0.5,
      },
      {
        field: 'vendorItemNumber',
        headerName: 'Vendor Item No.',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Vendor Item No."
            params={params}
            keyPrefix={ADD_INVOICE_COL_I18N}
          />
        ),
        editable: true,
        flex: 1,
      },
      {
        field: 'customerItemNumber',
        headerName: 'Customer Item No.',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Customer Item No."
            params={params}
            keyPrefix={ADD_INVOICE_COL_I18N}
          />
        ),
        editable: true,
        flex: 1,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'description',
        headerName: 'Description',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Description"
            params={params}
            keyPrefix={GLOBAL_COMMON_COL_I18N}
          />
        ),
        editable: true,
        flex: 1,
      },
      {
        field: 'commissionable',
        headerName: 'Commissionable',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Commissionable"
            params={params}
            keyPrefix={GLOBAL_COMMON_COL_I18N}
          />
        ),
        type: 'boolean',
        editable: true,
        flex: 1.2,
      },
      {
        field: 'commissionRate',
        headerName: 'Commission Rate',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Commission Rate"
            params={params}
            keyPrefix={GLOBAL_COMMON_COL_I18N}
          />
        ),
        type: 'number',
        editable: true,
        flex: 1.2,
        valueParser: (value) => {
          const num = Number(value);
          if (isNaN(num)) return undefined;
          return Math.max(0, Math.min(1, num));
        },
      },
      {
        field: 'taxable',
        headerName: 'Taxable',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Taxable"
            params={params}
            keyPrefix={GLOBAL_COMMON_COL_I18N}
          />
        ),
        type: 'boolean',
        editable: true,
        flex: 1,
      },
      {
        field: 'vendorCost',
        headerName: 'Vendor Cost',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Vendor Cost"
            params={params}
            keyPrefix={ADD_INVOICE_COL_I18N}
          />
        ),
        editable: true,
        type: 'number',
        flex: 1,
        headerAlign: 'left',
        valueParser: numberTypeController,
      },
      {
        field: 'customerPrice',
        headerName: 'Customer Price',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Customer Price"
            params={params}
            keyPrefix={ADD_INVOICE_COL_I18N}
          />
        ),
        editable: true,
        type: 'number',
        flex: 1,
        headerAlign: 'left',
        valueParser: numberTypeController,
      },
      {
        field: 'vendorAmount',
        headerName: 'Vendor Amount',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Vendor Amount"
            params={params}
            keyPrefix={ADD_INVOICE_COL_I18N}
          />
        ),
        flex: 1,
        editable: false,
        type: 'number',
        align: 'right',
        headerAlign: 'left',
        valueGetter: (value, row) => {
          const vendorCost = row.vendorCost ?? 0;
          const quantityOrdered = row.quantityOrdered ?? 0;
          return vendorCost * quantityOrdered;
        },
      },
      {
        field: 'customerAmount',
        headerName: 'Customer Amount',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Customer Amount"
            params={params}
            keyPrefix={ADD_INVOICE_COL_I18N}
          />
        ),
        flex: 1,
        editable: false,
        type: 'number',
        align: 'right',
        headerAlign: 'left',
        valueGetter: (value, row) => {
          const customerPrice = row.customerPrice ?? 0;
          const quantityOrdered = row.quantityOrdered ?? 0;
          return customerPrice * quantityOrdered;
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Actions"
            params={params}
            keyPrefix={GLOBAL_BUTTONS_COL_I18N}
          />
        ),
        width: 100,
        cellClassName: 'actions',
        getActions: ({ id }) => {
          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

          if (isInEditMode) {
            return [
              <GridActionsCellItem
                disabled={viewMode}
                icon={<SaveIcon />}
                label="Save"
                sx={{ color: 'primary.main' }}
                onClick={handleSaveClick(id)}
              />,
              <GridActionsCellItem
                disabled={viewMode}
                icon={<CancelIcon />}
                label="Cancel"
                className="textPrimary"
                onClick={handleCancelClick(id)}
                color="inherit"
              />,
            ];
          }

          return [
            <React.Fragment key={String(id)}>
              <IconButton
                size="small"
                disabled={viewMode}
                onClick={handleOpenActionsMenu(id)}
              >
                <MoreVertIcon />
              </IconButton>

              <Menu
                anchorEl={actionsMenuAnchors[String(id)] ?? null}
                open={Boolean(actionsMenuAnchors[String(id)])}
                onClose={handleCloseActionsMenu(id)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  disabled={viewMode}
                  onClick={() => {
                    handleEditClick(id)();
                    handleCloseActionsMenu(id)();
                  }}
                >
                  <ListItemIcon>
                    <EditIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Edit" />
                </MenuItem>

                <MenuItem
                  disabled={viewMode}
                  onClick={() => {
                    handleDeleteClick(id)();
                    handleCloseActionsMenu(id)();
                  }}
                >
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Delete" />
                </MenuItem>
              </Menu>
            </React.Fragment>,
          ];
        },
      },
    ],
    [rowModesModel, viewMode],
  );

  const persistedPrefs = usePersistedDataGridColumnPreferences({
    tableId: TABLE_ID,
    columns: baseColumns,
    apiRef,
    leadingGridColumnFields: viewMode
      ? EMPTY_LEADING_GRID_COLUMNS
      : GRID_CHECKBOX_COLUMN_ORDER,
  });

  const columns = useMemo(
    () => persistedPrefs.applyPersistedPreferences(baseColumns),
    [baseColumns, persistedPrefs.applyPersistedPreferences],
  );

  useEffect(() => {
    setItems(formik.values.items);
  }, [isOpenInvoiceModal, formik.values.items]);

  const totalVendorCost = items
    .reduce((acc, item) => acc + item.vendorCost * item.quantityOrdered, 0)
    .toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  const totalCustomerPrice = items
    .reduce((acc, item) => acc + item.customerPrice * item.quantityOrdered, 0)
    .toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  return (
    <>
      <Typography
        color="primary"
        className="flex justify-between"
        component="div"
      >
        <span>{tAddI('ITEMS')}</span>
        <Box className="flex">
          <Link
            className="mr-2"
            color="text.secondary"
            underline="none"
            sx={{ cursor: 'pointer' }}
          >
            {tAddI('TOTAL VENDOR COST') + ':'}
          </Link>
          <span className="font-bold text-black">{totalVendorCost}</span>
          <Link
            className="mx-2"
            color="text.secondary"
            underline="none"
            sx={{ cursor: 'pointer' }}
          >
            {tAddI('TOTAL CUSTOMER PRICE') + ':'}
          </Link>
          <span className="font-bold text-black">{totalCustomerPrice}</span>
        </Box>
      </Typography>
      <Divider sx={{ marginBottom: 1 }} />
      <Box sx={{ height: 500, width: '100%', overflowX: 'auto' }}>
        <DataGridPro
          apiRef={apiRef}
          columnHeaderHeight={56}
          disableRowSelectionOnClick
          disableColumnMenu={viewMode}
          disableDensitySelector={viewMode}
          disableColumnSelector={viewMode}
          disableMultipleRowSelection={viewMode}
          isCellEditable={(params) => !viewMode}
          checkboxSelection={!viewMode}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(newSelection) =>
            setSelectionModel(newSelection)
          }
          sx={{
            border: 0,
            '& .MuiDataGrid-cell': {
              padding: columnPadding,
            },
            '& .MuiDataGrid-columnHeader': {
              padding: columnPadding,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: 1.2,
            },
          }}
          onRowEditStop={handleRowEditStop}
          hideFooter
          disableColumnFilter
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          processRowUpdate={processRowUpdate}
          slots={{
            toolbar: (props) => (
              <EditToolbar
                {...props}
                selectionModel={selectionModel}
                setSelectionModel={setSelectionModel}
                items={items}
                setItems={setItems}
                formik={formik}
                viewMode={viewMode}
                onAddRow={handleAddRowClick}
              />
            ),
          }}
          rows={items.map((item) => ({
            ...item,
            id: item.itemId,
          }))}
          columns={columns}
          columnVisibilityModel={persistedPrefs.columnVisibilityModel}
          onColumnVisibilityModelChange={
            persistedPrefs.onColumnVisibilityModelChange
          }
          onColumnWidthChange={persistedPrefs.onColumnWidthChange}
          onColumnOrderChange={persistedPrefs.onColumnOrderChange}
          pinnedColumns={{
            right: ['actions'],
            ...(!viewMode ? { left: [GRID_CHECKBOX_SELECTION_FIELD] } : {}),
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 100 } },
          }}
          pageSizeOptions={[items?.length || 0]}
        />
      </Box>
    </>
  );
}
